import { useRef, useState } from 'react';
import identity from 'lodash/identity';
import { useController } from 'react-hook-form';

const fullMatch = /(\d{0,3})(\d{0,2})(\d{0,4})/;
const last4match = /(\d{0,4})/;

const findFirstNonMatchingIndex = (a: string, b: string) => {
  for (let i = 0, l = Math.max(a?.length, b?.length); i < l; i++) {
    if (a[i] !== b[i]) {
      return i;
    }
  }
  return undefined;
};

const findLastChangedIndex = (string: string, startIndex: number | undefined) => {
  if (!startIndex && startIndex !== 0) {
    return undefined;
  }
  if (startIndex === string.length - 1) {
    return startIndex;
  }
  const maskCharacters = ['-', '•'];
  for (let i = startIndex; i <= string.length; i++) {
    const currentChar = string[i];
    const nextChar = string[i + 1];
    const secondUpcomingChar = string[i + 2];

    const currentCharIsMasked = maskCharacters.includes(currentChar);
    const nextCharIsMasked = maskCharacters.includes(nextChar);
    const secondUpcomingCharIsMasked = maskCharacters.includes(secondUpcomingChar);

    const hasAnotherUnmaskedCharacter =
      !nextCharIsMasked || (nextChar === '-' && secondUpcomingCharIsMasked);
    if ((!currentCharIsMasked && !hasAnotherUnmaskedCharacter) || !currentChar) {
      return i;
    }
  }
  return startIndex;
};

const findNonMatchingInputIndexes = (oldValue: string, newValue: string) => {
  const firstNonMatchingIndex = findFirstNonMatchingIndex(oldValue, newValue);
  const lastChangedIndex = findLastChangedIndex(newValue, firstNonMatchingIndex);
  const numExtraChars = lastChangedIndex ? newValue.slice(lastChangedIndex + 1).length : 0;
  return { firstNonMatchingIndex, lastChangedIndex, numExtraChars };
};

export const useSSNField = (
  name: string,
  onChange: (...event: any) => void,
  last4only: boolean,
  parseValue: (value: any) => any = identity,
  defaultHidden = true,
) => {
  const maskValue = (value: string) => {
    const cardValue = value.replace(/\D/g, '').match(last4only ? last4match : fullMatch);
    if (cardValue) {
      const newValue = [cardValue[1], cardValue[2], cardValue[3]]
        .filter((value) => value?.length)
        .join('-');
      return newValue;
    }
    return '';
  };

  const hideValue = (value: string) => {
    const replacementValue = value.replace(/\d/g, '•');
    return replacementValue;
  };

  const {
    field: { value: fieldValue },
  } = useController({ name });
  const value = parseValue(fieldValue);
  const [maskedValue, setMaskedValue] = useState(value ? maskValue(value) : '');
  const [lastInputValue, setLastInputValue] = useState(value ? hideValue(maskValue(value)) : '');
  const [hidden, setHidden] = useState(defaultHidden);

  const inputRef = useRef<HTMLInputElement>(null);

  const updateValues = (newUnmaskedValue: string, newMaskedValue: string) => {
    setMaskedValue(newMaskedValue);
    updateInput(newMaskedValue, hidden);
    onChange(newUnmaskedValue);
  };

  const unmaskValue = (value: string) => value.replace(/-/g, '');

  const addNewEndingCharactersToInputValue = (inputValue: string) => {
    const newUnhiddenValue = `${maskedValue}${inputValue.slice(maskedValue.length)}`;
    const newUnmaskedValue = unmaskValue(newUnhiddenValue).slice(0, last4only ? 4 : 9);
    const newMaskedValue = maskValue(newUnmaskedValue);
    if (!newUnmaskedValue || !newMaskedValue) {
      return;
    }
    updateValues(newUnmaskedValue, newMaskedValue);
  };

  const removeCharactersFromEnd = (inputValue: string) => {
    const newUnmaskedValue = unmaskValue(inputValue).slice(0, 9);
    const newMaskedValue = maskValue(newUnmaskedValue);
    if (!newUnmaskedValue || !newMaskedValue) {
      return;
    }
    updateValues(newUnmaskedValue, newMaskedValue);
  };

  const areUnmaskedStringsEqual = (a: string, b: string) => {
    return unmaskValue(a) === unmaskValue(b);
  };

  const setSelectionRange = (selectionStartIdx: number, selectionEndIdx: number) => {
    if (inputRef.current) {
      inputRef.current.setSelectionRange(selectionStartIdx, selectionEndIdx);
    }
  };

  const changeMiddleCharacters = (
    inputValue: string,
    firstNonMatchingIndex: number,
    lastChangedIndex: number,
    numExtraChars: number,
  ) => {

    const unchangedBeginningCharacters = maskedValue.slice(0, firstNonMatchingIndex);
    const changedCharacters = inputValue.slice(firstNonMatchingIndex, lastChangedIndex + 1);
    const remainingEndCharacters = numExtraChars ? maskedValue.slice(-numExtraChars) : '';

    const updatedString = unchangedBeginningCharacters + changedCharacters + remainingEndCharacters;

    const newSubmitValue = unmaskValue(updatedString);
    const newMaskedValue = maskValue(newSubmitValue);

    updateValues(newSubmitValue, newMaskedValue);
  };

  const handleChange = () => {
    if (inputRef.current) {
      const inputValue = inputRef.current.value;
      const cursorPosition = inputRef.current.selectionStart;

      const { firstNonMatchingIndex, lastChangedIndex, numExtraChars } =
        findNonMatchingInputIndexes(lastInputValue, inputValue);

      if (firstNonMatchingIndex === undefined || lastChangedIndex === undefined) {
        return;
      }

      if (areUnmaskedStringsEqual(lastInputValue, inputValue)) {
        updateInput(maskedValue, hidden);
        if (cursorPosition) {
          setSelectionRange(cursorPosition, cursorPosition);
        }
        return;
      }

      if (lastChangedIndex < maskedValue.length && !inputValue.slice(lastChangedIndex + 1).length) {
        removeCharactersFromEnd(inputValue);
        return;
      }

      if (firstNonMatchingIndex >= maskedValue.length && !!inputValue[firstNonMatchingIndex]) {
        addNewEndingCharactersToInputValue(inputValue);
        return;
      }
      changeMiddleCharacters(inputValue, firstNonMatchingIndex, lastChangedIndex, numExtraChars);
      return;
    }
  };

  const updateInput = (maskedInputValue: string, hide: boolean) => {
    const newValue = hide ? hideValue(maskedInputValue) : maskedInputValue;
    if (inputRef.current) {
      inputRef.current.value = newValue;
      setLastInputValue(newValue);
    }
  };

  const changeHidden = () => {
    const newHiddenValue = !hidden;
    setHidden(newHiddenValue);
    updateInput(maskedValue, newHiddenValue);
  };

  return {
    changeHidden,
    handleChange,
    inputRef,
  };
};
